import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ShieldCheck, Warning, Plus, Trash, X, FileArrowDown, ArrowClockwise, ClockCountdown, CheckCircle } from "@phosphor-icons/react";

const CLOCK = {
  overdue: "bg-red-100 text-red-700 border-red-300",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  current: "bg-green-100 text-green-700 border-green-300",
  none: "bg-slate-100 text-slate-500 border-slate-300",
};
const clockLabel = (c) => {
  if (!c || c.status === "none") return "—";
  if (c.status === "overdue") return `Overdue ${Math.abs(c.days_left)}d`;
  if (c.status === "due_soon") return `Due in ${c.days_left}d`;
  return c.due ? `Due ${c.due}` : "On track";
};
const APPLIC = { applicable: "Applicable", not_applicable: "Not Applicable", superseded: "Superseded" };
const ACTION = { pending: "Pending", applied: "Applied", mitigation_plan: "Mitigation Plan" };

const Clock = ({ c, testid }) => (
  <span data-testid={testid} className={`text-xs px-2 py-0.5 rounded border ${CLOCK[c?.status || "none"]}`}>{clockLabel(c)}</span>
);

export default function Cip007Patch({ embedded = false }) {
  const { isRole } = useAuth();
  const canEdit = isRole("admin", "analyst");
  const [tab, setTab] = useState("sources");
  const [summary, setSummary] = useState(null);
  const [sources, setSources] = useState([]);
  const [evals, setEvals] = useState([]);
  const [patches, setPatches] = useState([]);
  const [assets, setAssets] = useState([]);
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(null); // {type, data}

  const loadAll = useCallback(async () => {
    try {
      const [su, sr, pa, as, st] = await Promise.all([
        api.get("/cip007/summary"), api.get("/cip007/patch-sources"),
        api.get("/cip007/patches"), api.get("/cip007/assets"), api.get("/cip007/settings"),
      ]);
      setSummary(su.data); setSources(sr.data.sources); setPatches(pa.data.patches);
      setAssets(as.data.assets); setSettings(st.data);
    } catch (e) { /* noop */ }
  }, []);
  const loadEvals = useCallback(() => api.get("/cip007/patch-evals").then((r) => setEvals(r.data.evaluations)).catch(() => {}), []);

  useEffect(() => { loadAll(); loadEvals(); api.post("/cip007/process/run").catch(() => {}); }, [loadAll, loadEvals]);

  const assetName = (id) => (assets.find((a) => a.id === id) || {}).name || id;

  const runProcess = async () => {
    try {
      const { data } = await api.post("/cip007/process/run");
      if (data.reason === "process-disabled") toast.info("Recurring process is disabled — enable it in Settings.");
      else toast.success(`ClaroFlow sync: ${data.created} ticket(s), ${data.alerts} alert(s)`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportR2 = async () => {
    try { await downloadBlob("/cip007/r2-export", "CIP007_R2_Patch_Management_Evidence.docx"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delSource = async (id) => { await api.delete(`/cip007/patch-sources/${id}`); toast.success("Source removed"); loadAll(); };
  const delPatch = async (id) => { await api.delete(`/cip007/patches/${id}`); toast.success("Patch removed"); loadAll(); };
  const approveMit = async (id) => { await api.post(`/cip007/patches/${id}/mitigation/approve`); toast.success("Mitigation plan approved by CIP Senior Manager"); loadAll(); };
  const reconcile = async () => {
    try {
      const { data } = await api.post("/cip007/reconcile");
      if (data.reason === "auto-close-disabled") toast.info("Auto-close is off — enable it in Settings.");
      else toast.success(`Reconciled ${data.assets_checked} asset(s): ${data.closed} action(s) auto-closed from CIP-010`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stats = summary || {};

  return (
    <div className="max-w-full p-6" data-testid="cip007-page">
      {!embedded && (
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck size={26} weight="duotone" /> CIP-007 R2 — Security Patch Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Track patch sources, run the 35-day evaluation cycle, and act within 35 days (apply or mitigation plan).</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1" onClick={runProcess} data-testid="c7-run-process"><ArrowClockwise size={14} /> Sync ClaroFlow</Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={exportR2} data-testid="c7-export"><FileArrowDown size={14} /> R2 Evidence (Word)</Button>
        </div>
      </div>
      )}
      {embedded && (
        <div className="flex justify-end gap-2 mb-4">
          <Button size="sm" variant="outline" className="gap-1" onClick={runProcess} data-testid="c7-run-process"><ArrowClockwise size={14} /> Sync ClaroFlow</Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={exportR2} data-testid="c7-export"><FileArrowDown size={14} /> R2 Evidence (Word)</Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="Patch Sources" value={stats.sources ?? "—"} />
        <Stat label="Evals Overdue" value={stats.evals_overdue ?? "—"} danger={stats.evals_overdue > 0} />
        <Stat label="Applicable Patches" value={stats.applicable_patches ?? "—"} />
        <Stat label="Actions Overdue" value={stats.actions_overdue ?? "—"} danger={stats.actions_overdue > 0} />
        <Stat label="Open Mitigations" value={stats.open_mitigations ?? "—"} />
      </div>

      <div className="flex gap-1 border-b border-border mb-4">
        {[["sources", "Patch Sources"], ["evals", "Evaluations"], ["patches", "Applicable Patches"], ["settings", "Settings"]].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); if (k === "evals") loadEvals(); }} data-testid={`c7-tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-primary font-semibold text-primary" : "border-transparent text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "sources" && (
        <div data-testid="c7-sources-tab">
          {canEdit && <Button size="sm" className="gap-1 mb-3" onClick={() => setModal({ type: "source", data: null })} data-testid="c7-add-source"><Plus size={14} /> Add Patch Source</Button>}
          <div className="space-y-2">
            {sources.length === 0 && <Empty text="No patch sources yet. Add the sources you track for cyber security patch releases (R2.1)." />}
            {sources.map((s) => (
              <div key={s.id} className="border border-border rounded p-3 flex items-center justify-between gap-3" data-testid={`c7-source-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-2">{s.name} <span className="text-xs text-muted-foreground font-normal">{s.vendor}</span>{!s.active && <span className="text-xs text-slate-400">(inactive)</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.url}</div>
                  <div className="text-xs mt-1">Covers: {(s.covers_asset_ids || []).map(assetName).join(", ") || "—"} · Last eval: {s.last_eval_date || "none"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Clock c={s.eval_clock} testid={`c7-source-clock-${s.id}`} />
                  {canEdit && <button onClick={() => setModal({ type: "eval", data: { source_id: s.id, source_name: s.name } })} className="text-xs text-primary underline" data-testid={`c7-log-eval-${s.id}`}>Log Eval</button>}
                  {canEdit && <button onClick={() => setModal({ type: "source", data: s })} className="text-xs text-muted-foreground underline">Edit</button>}
                  {canEdit && <button onClick={() => delSource(s.id)} className="text-red-500" data-testid={`c7-del-source-${s.id}`}><Trash size={15} /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "evals" && (
        <div data-testid="c7-evals-tab">
          <p className="text-xs text-muted-foreground mb-3">R2.2 — at least once every 35 calendar days, evaluate patches released since the last evaluation. Logging an evaluation resets that source's clock.</p>
          <table className="w-full text-sm"><thead><tr className="text-muted-foreground text-left border-b border-border">{["Date", "Source", "Evaluator", "Patches Reviewed", "Notes"].map((h) => <th key={h} className="py-1.5 px-2">{h}</th>)}</tr></thead>
            <tbody>
              {evals.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No evaluations logged.</td></tr>}
              {evals.map((e) => (
                <tr key={e.id} className="border-b border-border/50" data-testid={`c7-eval-${e.id}`}>
                  <td className="py-1.5 px-2 font-mono text-xs">{e.eval_date}</td><td className="px-2">{e.source_name}</td>
                  <td className="px-2">{e.evaluator}</td><td className="px-2">{e.patches_reviewed}</td><td className="px-2 text-xs text-muted-foreground">{e.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "patches" && (
        <div data-testid="c7-patches-tab">
          {canEdit && <div className="flex gap-2 mb-3">
            <Button size="sm" className="gap-1" onClick={() => setModal({ type: "patch", data: null })} data-testid="c7-add-patch" disabled={sources.length === 0}><Plus size={14} /> Add Patch</Button>
            <Button size="sm" variant="outline" className="gap-1" onClick={reconcile} data-testid="c7-reconcile"><ArrowClockwise size={14} /> Reconcile with CIP-010</Button>
          </div>}
          <div className="space-y-2">
            {patches.length === 0 && <Empty text="No patches recorded. After an evaluation, record applicable patches and their action within 35 days (R2.3)." />}
            {patches.map((p) => (
              <div key={p.id} className="border border-border rounded p-3" data-testid={`c7-patch-${p.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{p.patch_id} <span className="font-normal text-muted-foreground">{p.title}</span></div>
                    <div className="text-xs text-muted-foreground">{p.source_name} · {APPLIC[p.applicability]} · Evaluated {p.eval_date}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded border border-border bg-secondary">{ACTION[p.action]}</span>
                    {p.auto_closed && <span className="text-xs px-2 py-0.5 rounded border border-green-300 bg-green-100 text-green-700 inline-flex items-center gap-1" data-testid={`c7-autoclosed-${p.id}`} title={`Confirmed via CIP-010 baseline ${p.cip010_snapshot_id}`}><CheckCircle size={12} weight="fill" /> Auto-closed via CIP-010</span>}
                    <Clock c={p.action_clock} testid={`c7-patch-clock-${p.id}`} />
                  </div>
                </div>
                {p.mitigation && (
                  <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 flex items-center justify-between gap-2" data-testid={`c7-mit-${p.id}`}>
                    <div>Mitigation plan → complete by <b>{p.mitigation.planned_completion}</b> · {p.mitigation.status}
                      {p.mitigation.csm_approved ? <span className="text-green-700 ml-2 inline-flex items-center gap-1"><CheckCircle size={13} weight="fill" /> CSM approved ({p.mitigation.csm_name})</span>
                        : <span className="text-red-600 ml-2">Awaiting CIP Senior Manager approval</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock c={p.mitigation.clock} />
                      {canEdit && !p.mitigation.csm_approved && <button onClick={() => approveMit(p.id)} className="text-primary underline" data-testid={`c7-approve-mit-${p.id}`}>Approve</button>}
                    </div>
                  </div>
                )}
                {canEdit && (
                  <div className="flex gap-3 mt-2 text-xs">
                    <button onClick={() => setModal({ type: "patch", data: p })} className="text-primary underline" data-testid={`c7-edit-patch-${p.id}`}>Edit / Take Action</button>
                    <button onClick={() => delPatch(p.id)} className="text-red-500">Delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && settings && (
        <SettingsTab settings={settings} canEdit={canEdit} onSaved={loadAll} />
      )}

      {modal?.type === "source" && <SourceModal src={modal.data} assets={assets} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
      {modal?.type === "eval" && <EvalModal ctx={modal.data} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); loadEvals(); }} />}
      {modal?.type === "patch" && <PatchModal patch={modal.data} sources={sources} assets={assets} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
    </div>
  );
}

const Stat = ({ label, value, danger }) => (
  <div className={`rounded-lg border p-3 ${danger ? "border-red-300 bg-red-50" : "border-border bg-card"}`}>
    <div className={`text-2xl font-bold ${danger ? "text-red-600" : ""}`}>{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);
const Empty = ({ text }) => <div className="text-sm text-muted-foreground bg-secondary/40 border border-dashed border-border rounded p-6 text-center">{text}</div>;

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className={`bg-card rounded-lg shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-auto`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-card">
        <h2 className="font-semibold">{title}</h2>
        <button onClick={onClose}><X size={18} /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);
const Field = ({ label, children }) => <label className="block mb-3"><span className="block text-sm font-medium mb-1">{label}</span>{children}</label>;
const inputCls = "w-full border border-border rounded px-3 py-2 text-sm";

function SourceModal({ src, assets, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", vendor: "", url: "", covers_asset_ids: [], active: true, notes: "", ...(src || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const body = { name: f.name, vendor: f.vendor, url: f.url, covers_asset_ids: f.covers_asset_ids, active: f.active, notes: f.notes };
      if (src) await api.patch(`/cip007/patch-sources/${src.id}`, body); else await api.post("/cip007/patch-sources", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggleAsset = (id) => setF((p) => ({ ...p, covers_asset_ids: p.covers_asset_ids.includes(id) ? p.covers_asset_ids.filter((x) => x !== id) : [...p.covers_asset_ids, id] }));
  return (
    <Modal title={src ? "Edit Patch Source" : "Add Patch Source"} onClose={onClose}>
      <Field label="Source name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="c7-source-name" placeholder="e.g. Microsoft Update" /></Field>
      <Field label="Vendor"><input className={inputCls} value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} /></Field>
      <Field label="Tracking URL"><input className={inputCls} value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} /></Field>
      <Field label="Covered Cyber Assets">
        <div className="max-h-36 overflow-auto border border-border rounded p-2 space-y-1">
          {assets.length === 0 && <div className="text-xs text-muted-foreground">No CIP-002 assets found.</div>}
          {assets.map((a) => <label key={a.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.covers_asset_ids.includes(a.id)} onChange={() => toggleAsset(a.id)} /> {a.name}</label>)}
        </div>
      </Field>
      <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} disabled={busy} data-testid="c7-source-save">{busy ? "Saving…" : "Save"}</Button></div>
    </Modal>
  );
}

function EvalModal({ ctx, onClose, onSaved }) {
  const [f, setF] = useState({ eval_date: new Date().toISOString().slice(0, 10), evaluator: "", patches_reviewed: 0, notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/cip007/patch-evals", { source_id: ctx.source_id, ...f, patches_reviewed: Number(f.patches_reviewed) || 0 }); toast.success("Evaluation logged — clock reset"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Log Evaluation — ${ctx.source_name}`} onClose={onClose}>
      <Field label="Evaluation date"><input type="date" className={inputCls} value={f.eval_date} onChange={(e) => setF({ ...f, eval_date: e.target.value })} data-testid="c7-eval-date" /></Field>
      <Field label="Evaluator"><input className={inputCls} value={f.evaluator} onChange={(e) => setF({ ...f, evaluator: e.target.value })} placeholder="defaults to you" /></Field>
      <Field label="Patches reviewed"><input type="number" className={inputCls} value={f.patches_reviewed} onChange={(e) => setF({ ...f, patches_reviewed: e.target.value })} /></Field>
      <Field label="Notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} disabled={busy} data-testid="c7-eval-save">{busy ? "Saving…" : "Log Evaluation"}</Button></div>
    </Modal>
  );
}

function PatchModal({ patch, sources, assets, onClose, onSaved }) {
  const [f, setF] = useState({
    source_id: sources[0]?.id || "", patch_id: "", title: "", eval_date: new Date().toISOString().slice(0, 10),
    applicability: "applicable", action: "pending", applied_date: "", cip010_snapshot_id: "", notes: "",
    mit_enabled: false, planned_completion: "", mit_description: "", mit_status: "open",
    ...(patch ? {
      ...patch, mit_enabled: !!patch.mitigation, planned_completion: patch.mitigation?.planned_completion || "",
      mit_description: patch.mitigation?.description || "", mit_status: patch.mitigation?.status || "open",
    } : {}),
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.source_id) return toast.error("Select a source");
    if (!f.patch_id.trim()) return toast.error("Patch ID is required");
    if (f.action === "mitigation_plan" && !f.planned_completion) return toast.error("Mitigation plans need a planned completion date");
    setBusy(true);
    const body = {
      source_id: f.source_id, patch_id: f.patch_id, title: f.title, eval_date: f.eval_date,
      applicability: f.applicability, action: f.action, applied_date: f.applied_date || null,
      cip010_snapshot_id: f.cip010_snapshot_id, notes: f.notes,
      mitigation: (f.action === "mitigation_plan" || f.mit_enabled) && f.planned_completion
        ? { planned_completion: f.planned_completion, description: f.mit_description, status: f.mit_status,
            csm_approved: patch?.mitigation?.csm_approved || false, csm_name: patch?.mitigation?.csm_name || "" }
        : null,
    };
    try { if (patch) await api.patch(`/cip007/patches/${patch.id}`, body); else await api.post("/cip007/patches", body); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Modal title={patch ? "Edit Patch / Take Action" : "Add Patch"} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Source"><select className={inputCls} value={f.source_id} onChange={(e) => setF({ ...f, source_id: e.target.value })} data-testid="c7-patch-source">{sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
        <Field label="Patch ID (KB / bulletin)"><input className={inputCls} value={f.patch_id} onChange={(e) => setF({ ...f, patch_id: e.target.value })} data-testid="c7-patch-id" /></Field>
      </div>
      <Field label="Title / description"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Evaluation date (starts 35-day action clock)"><input type="date" className={inputCls} value={f.eval_date} onChange={(e) => setF({ ...f, eval_date: e.target.value })} data-testid="c7-patch-eval-date" /></Field>
        <Field label="Applicability"><select className={inputCls} value={f.applicability} onChange={(e) => setF({ ...f, applicability: e.target.value })} data-testid="c7-patch-applicability">{Object.entries(APPLIC).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
      </div>
      <Field label="Action (within 35 days)"><select className={inputCls} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} data-testid="c7-patch-action">{Object.entries(ACTION).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
      {f.action === "applied" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applied date"><input type="date" className={inputCls} value={f.applied_date || ""} onChange={(e) => setF({ ...f, applied_date: e.target.value })} data-testid="c7-applied-date" /></Field>
          <Field label="CIP-010 post-change snapshot (optional link)"><input className={inputCls} value={f.cip010_snapshot_id} onChange={(e) => setF({ ...f, cip010_snapshot_id: e.target.value })} placeholder="snapshot id from CIP-010 delta" /></Field>
        </div>
      )}
      {f.action === "mitigation_plan" && (
        <div className="border border-amber-200 bg-amber-50 rounded p-3 mb-3">
          <Field label="Planned completion date"><input type="date" className={inputCls} value={f.planned_completion} onChange={(e) => setF({ ...f, planned_completion: e.target.value })} data-testid="c7-mit-completion" /></Field>
          <Field label="Mitigation description"><textarea className={inputCls} rows={2} value={f.mit_description} onChange={(e) => setF({ ...f, mit_description: e.target.value })} /></Field>
          <Field label="Status"><select className={inputCls} value={f.mit_status} onChange={(e) => setF({ ...f, mit_status: e.target.value })}><option value="open">Open</option><option value="implemented">Implemented</option></select></Field>
          <p className="text-xs text-muted-foreground">CIP Senior Manager approval (required for any timeframe extension, R2.4) is captured with the "Approve" action on the patch card.</p>
        </div>
      )}
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} disabled={busy} data-testid="c7-patch-save">{busy ? "Saving…" : "Save"}</Button></div>
    </Modal>
  );
}

function SettingsTab({ settings, canEdit, onSaved }) {
  const [f, setF] = useState(settings);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/cip007/settings", f); toast.success("Settings saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg" data-testid="c7-settings-tab">
      <p className="text-sm text-muted-foreground mb-4">When enabled, ClaroFlow raises tickets and detective PNC alerts for overdue 35-day evaluation cycles and overdue patch actions.</p>
      <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={f.process_enabled} onChange={(e) => setF({ ...f, process_enabled: e.target.checked })} data-testid="c7-settings-enabled" /> Enable recurring ClaroFlow process</label>
      <label className="flex items-start gap-2 text-sm mb-3"><input type="checkbox" className="mt-1" checked={f.auto_close_from_cip010 !== false} onChange={(e) => setF({ ...f, auto_close_from_cip010: e.target.checked })} data-testid="c7-settings-autoclose" /> <span>Auto-close R2 actions when a CIP-010 post-change baseline confirms the patch (KB) landed <span className="text-xs text-muted-foreground">(recommended, default on)</span></span></label>
      <Field label="Process assignee (SME name or email)"><input className={inputCls} value={f.process_assignee} onChange={(e) => setF({ ...f, process_assignee: e.target.value })} data-testid="c7-settings-assignee" /></Field>
      <Field label="CIP Senior Manager (name or email)"><input className={inputCls} value={f.csm_name} onChange={(e) => setF({ ...f, csm_name: e.target.value })} data-testid="c7-settings-csm" /></Field>
      {canEdit && <Button size="sm" onClick={save} disabled={busy} data-testid="c7-settings-save">{busy ? "Saving…" : "Save Settings"}</Button>}
    </div>
  );
}
